import { SidebarInset, TooltipProvider } from "@beweegbaas/ui";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { EnvChromeShell } from "#/modules/admin/components/admin-banners";
import { AdminSidebar } from "#/modules/admin/components/admin-sidebar";
import { AppSidebarProvider } from "#/modules/layout/sidebar-provider";
import { getAdminUnreadTicketCountFn } from "#/modules/tickets/tickets-admin.functions";

// Admin layout + guard (portal's `_protected.admin`). Non-admins are bounced to login.
// Pure synchronous check against the root-resolved auth context (no RPC).
export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin")({
  beforeLoad: ({ context, params }) => {
    if (!context.isAdmin) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/login",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }

    // 2FA guard, mirroring the `$school` tree: an admin always needs 2FA (the backend reports
    // requires2FA for the Admin role regardless of any verwerkersovereenkomst).
    if (context.requires2FA) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/setup-2fa",
        params: { locale: params.locale, schoolyear: params.schoolyear },
      });
    }
  },
  // Feeds the "Tickets" sidebar badge — cheap count-only endpoint, refreshed by `router.invalidate()`
  // whenever a ticket is marked read (see the tickets drawer).
  loader: () => getAdminUnreadTicketCountFn(),
  component: AdminLayout,
});

// Mirrors DefaultLayout's chrome exactly: transparent wrapper over the page gradient, plain
// bg-background inset, no header bar (sidebar toggling goes via the rail / cmd+B).
function AdminLayout() {
  const unreadTicketCount = Route.useLoaderData();

  return (
    <TooltipProvider>
      <EnvChromeShell always>
        <AppSidebarProvider className="has-data-[variant=inset]:bg-transparent">
          <AdminSidebar unreadTicketCount={unreadTicketCount} />
          <SidebarInset className="min-w-0">
            <div id="main-content" tabIndex={-1} className="flex grow flex-col">
              <Outlet />
            </div>
          </SidebarInset>
        </AppSidebarProvider>
      </EnvChromeShell>
    </TooltipProvider>
  );
}
